import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { InstallationAddress } from "@/components/installations/InstallationAddress";
import type { InstallationIdentityLike } from "@/lib/installationContext";
import { resolveInstallationTitle, resolveInstallationCodeLabel } from "@/lib/installationContext";

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  in_progress: "En curso",
  completed: "Completada",
  cancelled: "Cancelada",
};

export function ContractInstallations({ contractId }: { contractId: number }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const query = trpc.customers.contracts.listInstallations.useQuery({ contractId }, { enabled: !!contractId });

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">No se pudieron cargar las instalaciones del contrato.</CardContent>
      </Card>
    );
  }

  const list = (query.data ?? []) as Array<Omit<InstallationIdentityLike, "id"> & {
    installationId: number;
    installationCode?: string | null;
    clientId?: string | null;
    name: string | null;
    workDescription: string | null;
    siteId: number | null;
    siteName: string | null;
    status: string | null;
    contractId: number;
  }>;
  const canEditOperational = ["admin", "project_manager", "admin_manager"].includes(user?.role || "");

  if (list.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Este contrato no tiene instalaciones asociadas todavía. Se añadirán desde el flujo de instalaciones del contrato.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {list.map((inst) => {
        const codeLabel = resolveInstallationCodeLabel(inst);
        return (
          <Card key={inst.installationId} className="cursor-pointer hover:border-primary/40" onClick={() => setLocation(`/installations/${inst.installationId}`)}>
            <CardContent className="flex flex-wrap items-center justify-between gap-2 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium">{codeLabel}</span>
                  {inst.status ? <Badge variant="outline" className="text-xs">{STATUS_LABELS[inst.status] ?? "Estado registrado"}</Badge> : null}
                </div>
                <div className="mt-1 text-sm">{resolveInstallationTitle({ ...inst, id: inst.installationId })}</div>
                <div className="text-xs text-muted-foreground">{inst.siteName ?? "Sin sede"}</div>
                <div className="mt-1 text-xs text-muted-foreground"><InstallationAddress installation={{ ...inst, id: inst.installationId }} /></div>
              </div>
              <div className="flex items-center gap-1">
                {canEditOperational && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      const returnTo = `${window.location.pathname}${window.location.search}`;
                      setLocation(`/installations?edit=${inst.installationId}&returnTo=${encodeURIComponent(returnTo)}`);
                    }}
                  >
                    Editar
                  </Button>
                )}
                <Button variant="ghost" size="sm">Ver instalación →</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
